import React, { useState, useEffect } from 'react';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface ColorCollectionFormSliderProps {
  workspaceId: string;
  colorCollectionId?: string;
  projectId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Project {
  _id: string;
  name: string;
}

interface ColorCollectionFormData {
  name: string;
  description: string;
  projectId: string;
  isActive: boolean;
  order: number;
}

const ColorCollectionFormSlider: React.FC<ColorCollectionFormSliderProps> = ({
  workspaceId,
  colorCollectionId,
  projectId,
  onSuccess,
  onCancel
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [nextOrder, setNextOrder] = useState<number>(0);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  const initialFormData: ColorCollectionFormData = {
    name: '',
    description: '',
    projectId: projectId || '',
    isActive: true,
    order: nextOrder
  };

  useEffect(() => {
    fetchProjects();
  }, [workspaceId]);

  useEffect(() => {
    if (projectId && !colorCollectionId) {
      fetchNextOrder(projectId);
    }
  }, [projectId, colorCollectionId, workspaceId]);

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchNextOrder = async (selectedProjectId: string, updateCallback?: (order: number) => void) => {
    try {
      setIsLoadingOrder(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-color-collections`, {
        
        params: {
          projectId: selectedProjectId,
          limit: 1,
          sortBy: 'order',
          sortOrder: 'desc'
        }
      });

      // Get the highest order value and add 1
      const collections = response.data.colorCollections;
      const nextOrderValue = collections && collections.length > 0 ? collections[0].order + 1 : 0;

      setNextOrder(nextOrderValue);
      if (updateCallback) {
        updateCallback(nextOrderValue);
      }
    } catch (error) {
      console.error('Error fetching next order:', error);
      setNextOrder(0);
      if (updateCallback) {
        updateCallback(0);
      }
    } finally {
      setIsLoadingOrder(false);
    }
  };

  const validateForm = (data: ColorCollectionFormData): string | null => {
    if (!data.name.trim()) {
      return 'Name is required';
    }
    if (!data.projectId) {
      return 'Project is required';
    }
    return null;
  };

  const transformFetchData = (response: any): ColorCollectionFormData => {
    const colorCollection = response.colorCollection;
    return {
      name: colorCollection.name || '',
      description: colorCollection.description || '',
      projectId: colorCollection.projectId || '',
      isActive: colorCollection.isActive ?? true,
      order: colorCollection.order || 0
    };
  };

  const transformSubmitData = (data: ColorCollectionFormData) => {
    return {
      name: data.name.trim(),
      description: data.description.trim() || undefined,
      projectId: data.projectId,
      isActive: data.isActive,
      order: data.order
    };
  };

  // Don't render the form until projects and order are loaded for new color collections
  if (!colorCollectionId && (loadingProjects || (projectId && isLoadingOrder))) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={`color-collection-form-${nextOrder}`}
      workspaceId={workspaceId}
      itemId={colorCollectionId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/pkg-color-collections`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="color-collection-form"
    >
      {({ formData, handleInputChange, handleFieldChange, loading }) => (
        <>
          <div className="form-group">
            <label htmlFor="projectId" className="form-label">
              Project
            </label>
            {loadingProjects ? (
              <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                <div className="builders-loading-spinner" />
                <p style={{ marginTop: '10px', fontSize: '13px', color: '#6B7280' }}>Loading projects...</p>
              </div>
            ) : (
              <>
                <select
                  id="projectId"
                  name="projectId"
                  className="form-input"
                  value={formData.projectId}
                  onChange={(e) => {
                    handleInputChange(e);
                    if (!colorCollectionId && e.target.value) {
                      fetchNextOrder(e.target.value, (order) => {
                        handleFieldChange('order', order);
                      });
                    }
                  }}
                  required
                  disabled={loading || !!colorCollectionId}
                  autoFocus
                >
                  <option value="">Select Project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
                {colorCollectionId && (
                  <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
                    Project cannot be changed after creation
                  </small>
                )}
              </>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter color collection name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              className="form-input"
              value={formData.description}
              onChange={handleInputChange}
              disabled={loading}
              placeholder="Enter description"
              rows={3}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
            <div className="form-group">
              <label htmlFor="order" className="form-label">
                Order
              </label>
              <input
                type="number"
                id="order"
                name="order"
                className="form-input"
                value={formData.order}
                onChange={handleInputChange}
                disabled={true}
                min="0"
              />
              <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
                Auto-generated based on existing collections
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="isActive" className="form-label">
                Status
              </label>
              <select
                id="isActive"
                name="isActive"
                className="form-input"
                value={formData.isActive ? 'active' : 'inactive'}
                onChange={(e) => handleFieldChange('isActive', e.target.value === 'active')}
                disabled={loading}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default ColorCollectionFormSlider;
